/**
 * Mounts ViewIndexView for an active view carrying an `index` clause. Same shape
 * as ViewGridContainer: the projection comes from the SAME machinery the board
 * uses (`useViewSurface` → the EFFECTIVE view + eval ctx), so governance, live
 * knobs, view-INSTANCE parent resolution, the plane pick, and the crash guard
 * all apply. `computeIndexData` orders the shown modules; this container resolves
 * each one to a **focal path into the shared solved board root** so its item can
 * render the module's real subspace as a live `<EmbeddedBoard>` (the master-plan
 * index — live previews over ONE shared model/solve, not static pictures).
 *
 * The render is CAPPED at {@link INDEX_RENDER_CAP} with a "showing N of M" count
 * so a huge index never tries to place thousands of item slots at once; within
 * the cap, {@link LazyMount} keeps only on-screen boards mounted (O(visible)).
 */
import { useCallback, useMemo, type ReactNode } from "react";
import { computeIndexData } from "@archlang/engine";
import { createBoardEngine, engineSource } from "@archlang/scene/board";
import { flattenModulesWithParent, type DependencyEdge, type Module } from "@archlang/scene/arch-model";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { LayoutContext, useLayoutContextOptional } from "../workbench/LayoutContext.js";
import { useOptionalWorkspace } from "../workbench/WorkspaceContext.js";
import { useViewSurface } from "./useViewSurface.js";
import { ViewIndexView } from "./ViewIndexView.js";
import { buildIndexItems, indexModulesByKey, moduleDegrees, type IndexItemVM } from "./indexItems.js";

/** Lazy solve depth for the index's shared board — a preview + a couple of
 *  levels, like the main board (`useBoardLayout` RENDER_DEPTH). */
const INDEX_BOARD_DEPTH = 3;

/** Max items resolved into VMs — a high ceiling, not a display limit: the view
 *  ROW-VIRTUALIZES, so the DOM cost is O(visible) regardless. This only bounds VM
 *  construction for a pathological model; the count note surfaces any overflow
 *  (never a silent cut). */
export const INDEX_RENDER_CAP = 10000;

/** The live index item's box. Height sets the row + the square board preview's
 *  side; width is the min column width when `columns` packs items into a grid. */
export const INDEX_ITEM_WIDTH = 320;
export const INDEX_ITEM_HEIGHT = 180;

export function ViewIndexContainer(): ReactNode {
  const { view, enginePlan, ctx } = useViewSurface();
  const { resolvedModel, model, edges } = useWorkspaceModel();
  const layout = useLayoutContextOptional();
  const store = useOptionalWorkspace();

  // The index's items show each module's REAL subspace, so their embedded
  // boards must project the FULL model — NOT the active view's projected root
  // (`layout.root` is filtered by the index view's `show`, so a container's
  // children would be missing). Build a full-model board source (one lazy engine,
  // all subspaces) and hand it to the embeds via an overriding LayoutContext.
  const indexBoardRoot = useMemo(() => {
    if (!model) return null;
    const flat = flattenModulesWithParent(model.modules as unknown as Module[]);
    const engine = createBoardEngine(flat, edges as unknown as DependencyEdge[]);
    return engineSource(engine, INDEX_BOARD_DEPTH);
  }, [model, edges]);

  // Reuse the app's controller (its model-derived `moduleFocalPath` is
  // projection-independent for shown modules) but swap in the full-model root.
  const indexBoardLayout = useMemo(
    () => (layout && indexBoardRoot ? { ...layout, root: indexBoardRoot } : layout),
    [layout, indexBoardRoot],
  );

  const data = useMemo(() => {
    if (!view || !ctx) return null;
    try {
      return computeIndexData(view, enginePlan, ctx);
    } catch (e) {
      // Same crash posture as the board's plan computation — degrade, never
      // unmount the tree.
      console.debug("[view-index] index data evaluation failed:", e);
      return null;
    }
  }, [view, enginePlan, ctx]);

  const modulesByKey = useMemo(
    () => (resolvedModel ? indexModulesByKey(resolvedModel) : null),
    [resolvedModel],
  );

  // Dependency degree (depends-on / used-by) for every module, from the FULL edge
  // graph — a per-item architectural fact. Computed once, reused across items.
  const degrees = useMemo(
    () => moduleDegrees(edges as unknown as DependencyEdge[]),
    [edges],
  );

  // Resolve each shown module to a focal path into the ONE shared solved root
  // (its containing space + its own `::space`; leaf ⇒ null) plus its detail facts.
  // Pure + tested in indexItems.ts against a real board.
  const items = useMemo<readonly IndexItemVM[]>(
    () =>
      data && modulesByKey
        ? buildIndexItems(data.entries, modulesByKey, layout?.moduleFocalPath, INDEX_RENDER_CAP, degrees)
        : [],
    [data, modulesByKey, layout, degrees],
  );

  // Click-through: open the module's dedicated PAGE (its rich detail), NOT a
  // board — the board-tab stub read as an empty card because nothing was selected
  // on it. The page (ModulePage) renders the full inspector; "Open board" there
  // drops to the board when wanted.
  const onOpen = useCallback(
    (moduleId: string, moduleName: string) => {
      store?.openTab({ kind: "module", moduleId, moduleName });
    },
    [store],
  );

  if (!data) return null;
  return (
    <LayoutContext.Provider value={indexBoardLayout}>
      <ViewIndexView
        items={items}
        total={data.entries.length}
        columns={data.columns}
        itemWidth={INDEX_ITEM_WIDTH}
        itemHeight={INDEX_ITEM_HEIGHT}
        onOpen={onOpen}
        focus={enginePlan?.focus}
      />
    </LayoutContext.Provider>
  );
}
